import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Feedback route — /master-order-testing/feedback
// Only reachable AFTER the 24h test window ends. Server enforces the rule
// (application status must be completed_test), and this component enforces
// it again in the UI so no user ever sees the form prematurely.

const PUBLICATION_OPTIONS = [
  { value: "with_name",     label: "Да, с моим именем" },
  { value: "anonymous",     label: "Да, без имени" },
  { value: "internal_only", label: "Нет, только для внутренней обратной связи" },
];

function EyebrowTitle({ eyebrow, title }) {
  return (
    <header className="mb-8">
      <div
        className="text-[10.5px] tracking-[0.3em] uppercase mb-3"
        style={{ color: "#8B6F4B" }}
        data-testid="mo-feedback-eyebrow"
      >
        {eyebrow}
      </div>
      <h1
        className="leading-[1.1]"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14", fontSize: "clamp(28px, 4.6vw, 40px)" }}
        data-testid="mo-feedback-title"
      >
        {title}
      </h1>
    </header>
  );
}

function PageFrame({ children }) {
  return (
    <main
      className="min-h-screen"
      style={{ background: "#EFE6D5" }}
      data-testid="mo-feedback-page"
    >
      <div className="mx-auto px-5 sm:px-10 py-12 sm:py-16" style={{ maxWidth: 880 }}>
        {children}
      </div>
    </main>
  );
}

function KeySignature() {
  return (
    <div className="mt-14 flex justify-center opacity-60" aria-hidden="true">
      <img src="/favicon-48x48.png" alt="" width={22} height={22} className="opacity-80" style={{ filter: "saturate(0.85)" }} />
    </div>
  );
}

function Label({ children, required }) {
  return (
    <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>
      {children}{required && <span aria-hidden="true"> ·</span>}
    </div>
  );
}

function HelperText({ children }) {
  return <div className="mt-2 text-[12.5px] leading-[1.5]" style={{ color: "#8B6F4B" }}>{children}</div>;
}

function FieldError({ children }) {
  return <div className="mt-2 text-[12.5px]" style={{ color: "#8B3E2A" }} role="alert">{children}</div>;
}

export default function MasterOrderTestingFeedback() {
  const nav = useNavigate();
  const { user, initializing } = useAuth();

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [experience, setExperience] = useState("");
  const [testimonial, setTestimonial] = useState("");
  const [pubPermission, setPubPermission] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Обратная связь — Мастер заказа — Anokina Studio";
    return () => { document.title = prev; };
  }, []);

  // Auth guard with return-path handoff.
  useEffect(() => {
    if (initializing) return;
    if (!user) {
      try { sessionStorage.setItem("post_login_redirect", "/master-order-testing/feedback"); } catch {}
      nav("/login", { replace: true });
    }
  }, [initializing, user, nav]);

  // Load application state — only completed_test testers may see the form.
  useEffect(() => {
    if (initializing || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/master-order-testing/applications/mine");
        if (!cancelled) setApplication(data || null);
      } catch { /* handled by ProtectedRoute or shown below */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [initializing, user]);

  const eligibility = useMemo(() => {
    if (!application) return "no_application";
    return application.status; // new | accepted | active_test | completed_test | feedback_received | declined
  }, [application]);

  const validate = () => {
    const errs = {};
    if (!experience.trim()) errs.experience = "Пожалуйста, расскажите о Вашем опыте.";
    if (!pubPermission) errs.pubPermission = "Пожалуйста, выберите вариант.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/master-order-testing/feedback", {
        experience_text: experience.trim(),
        short_testimonial_text: testimonial.trim() || null,
        publication_permission: pubPermission,
      });
      setSubmitted(true);
      toast.success("Обратная связь отправлена");
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 409 && code === "feedback_exists") {
        setSubmitted(true); // second attempt from another tab → show thank-you
      } else {
        toast.error(formatApiError(err) || "Не удалось отправить обратную связь");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (initializing || !user) return null;

  if (loading) {
    return (
      <PageFrame>
        <div className="py-16 text-[13px]" style={{ color: "#8B6F4B" }} data-testid="mo-feedback-loading">
          Загружаем состояние обратной связи…
        </div>
      </PageFrame>
    );
  }

  // Success state — either just submitted or already existed (409).
  if (submitted || eligibility === "feedback_received") {
    return (
      <PageFrame>
        <section data-testid="mo-feedback-thanks-state" className="mt-2">
          <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Спасибо за обратную связь" />
          <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
            <p>
              Ваши впечатления помогут нам сделать Мастер заказа ещё понятнее и удобнее
              для реальной работы кондитера.
            </p>
          </div>
          <div className="mt-10">
            <button
              type="button"
              data-testid="mo-feedback-back-to-hall"
              onClick={() => nav("/hall")}
              className="h-11 px-6 rounded-full text-[13px]"
              style={{ background: "#4A3728", color: "#F7EFE0" }}
            >
              Вернуться в Anokina Studio
            </button>
          </div>
          <KeySignature />
        </section>
      </PageFrame>
    );
  }

  // Not-eligible states — send the user back to the testing page.
  if (eligibility !== "completed_test") {
    return (
      <PageFrame>
        <section data-testid="mo-feedback-not-eligible" className="mt-2">
          <EyebrowTitle
            eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА"
            title="Обратная связь пока недоступна"
          />
          <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
            <p>
              Форма обратной связи открывается после завершения 24-часового тестового доступа
              к Мастеру заказа.
            </p>
          </div>
          <div className="mt-10">
            <button
              type="button"
              data-testid="mo-feedback-go-to-testing"
              onClick={() => nav("/master-order-testing")}
              className="h-11 px-6 rounded-full text-[13px]"
              style={{ background: "#4A3728", color: "#F7EFE0" }}
            >
              Перейти к странице тестирования
            </button>
          </div>
          <KeySignature />
        </section>
      </PageFrame>
    );
  }

  // Main feedback form.
  return (
    <PageFrame>
      <EyebrowTitle
        eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА"
        title="Расскажите о Вашем опыте"
      />
      <div className="max-w-[62ch] text-[15px] leading-[1.7]" style={{ color: "#3E2E20" }} data-testid="mo-feedback-intro">
        <p>Нам важна честная обратная связь после знакомства с Мастером заказа.</p>
      </div>

      <form onSubmit={onSubmit} className="mt-12 max-w-[62ch] space-y-8" noValidate data-testid="mo-feedback-form">
        {/* Experience — required */}
        <div>
          <Label required>Расскажите, как прошло знакомство с Мастером заказа</Label>
          <textarea
            data-testid="mo-feedback-experience-textarea"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            rows={7}
            placeholder="Что Вам понравилось? Что оказалось особенно удобным или полезным? Где было непонятно или слишком сложно? Чего Вам не хватило? Что показалось лишним? Хотели бы Вы пользоваться таким инструментом в своей работе дальше?"
            className="mt-2 w-full px-4 py-3 rounded-md text-[14px] bg-white outline-none focus:ring-2 resize-y"
            style={{ boxShadow: "inset 0 0 0 1px #E4D3B0", color: "#2A1D14" }}
          />
          {errors.experience && <FieldError>{errors.experience}</FieldError>}
        </div>

        {/* Short testimonial — optional */}
        <div>
          <Label>Короткий отзыв</Label>
          <textarea
            data-testid="mo-feedback-testimonial-textarea"
            value={testimonial}
            onChange={(e) => setTestimonial(e.target.value)}
            rows={3}
            placeholder="Если хотите, напишите одну-две фразы о Мастере заказа так, как рассказали бы о нём другому кондитеру."
            className="mt-2 w-full px-4 py-3 rounded-md text-[14px] bg-white outline-none focus:ring-2 resize-y"
            style={{ boxShadow: "inset 0 0 0 1px #E4D3B0", color: "#2A1D14" }}
          />
          <HelperText>Опционально.</HelperText>
        </div>

        {/* Publication permission — required single choice */}
        <div data-testid="mo-feedback-publication-group">
          <Label required>Можно ли использовать Ваш отзыв или его фрагмент в материалах Anokina Studio?</Label>
          <div className="mt-3 flex flex-col gap-2">
            {PUBLICATION_OPTIONS.map((opt) => {
              const selected = pubPermission === opt.value;
              return (
                <label
                  key={opt.value}
                  data-testid={`mo-feedback-publication-option-${opt.value}`}
                  className="flex items-center gap-3 cursor-pointer h-11 px-4 rounded-md text-[14px]"
                  style={{
                    background: selected ? "#F1E4CC" : "#FFFFFF",
                    color: "#2A1D14",
                    boxShadow: `inset 0 0 0 1px ${selected ? "#C7A97A" : "#E4D3B0"}`,
                  }}
                >
                  <input
                    type="radio"
                    name="publication_permission"
                    checked={selected}
                    onChange={() => setPubPermission(opt.value)}
                    className="accent-[#4A3728]"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          {errors.pubPermission && <FieldError>{errors.pubPermission}</FieldError>}
        </div>

        <div className="pt-2">
          <button
            data-testid="mo-feedback-submit-button"
            type="submit"
            disabled={submitting}
            className="h-11 px-8 rounded-full text-[13.5px] transition-opacity"
            style={{
              background: "#4A3728",
              color: "#F7EFE0",
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            {submitting ? "Отправляем…" : "Отправить обратную связь"}
          </button>
        </div>
      </form>

      <KeySignature />
    </PageFrame>
  );
}
